import { useParams, Link } from "react-router-dom";
import { Printer, ArrowLeft } from "lucide-react";
import { BRAND } from "@avestate/shared";
import { useProforma } from "@/hooks/useDeals.js";
import { Button } from "@/components/ui/button.js";

// Pro-forma provvigione: documento stampabile (window.print). NON è una
// fattura: è il riepilogo che l'agenzia gira al commercialista/cliente.

const eur = (n: number) => "€ " + n.toLocaleString("it-IT", { minimumFractionDigits: 2 });

export default function DealProforma(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const { data, isLoading } = useProforma(id);

  if (isLoading || !data) return <p className="p-8 text-ink-mute">{isLoading ? "Carico…" : "Pratica non trovata."}</p>;
  const { deal, seller, buyer, listing } = data;
  const c = deal.commissions;

  return (
    <div className="mx-auto max-w-[760px] space-y-6">
      <div className="flex items-center justify-between print:hidden">
        <Link to="/deals" className="inline-flex items-center gap-1 text-body text-ink-soft hover:text-ink"><ArrowLeft className="size-4" /> Pratiche</Link>
        <Button onClick={() => window.print()}><Printer className="size-4" /> Stampa / PDF</Button>
      </div>

      <div className="rounded border border-rule bg-surface p-8 print:border-0 print:bg-white print:text-black">
        <header className="mb-8 flex items-start justify-between">
          <div>
            <p className="font-display text-[22px] font-bold">{BRAND.name}</p>
            <p className="text-meta text-ink-mute print:text-black">{BRAND.legal.addressShort} · P.IVA {BRAND.legal.piva}</p>
          </div>
          <div className="text-right">
            <p className="text-body-strong">PRO-FORMA PROVVIGIONE</p>
            <p className="font-mono text-meta text-ink-mute print:text-black">pratica {deal.id.slice(0, 8)} · {new Date(data.generated_at).toLocaleDateString("it-IT")}</p>
          </div>
        </header>

        <section className="mb-6 space-y-1">
          <Row k="Pratica" v={deal.title} />
          <Row k="Fase" v={deal.stage} />
          {listing ? <Row k="Immobile" v={`${listing.title ?? listing.slug}${listing.city ? " · " + listing.city : ""}`} /> : null}
          {deal.price != null ? <Row k="Prezzo" v={eur(deal.price)} /> : null}
          {deal.closed_at ? <Row k="Chiusura" v={new Date(deal.closed_at).toLocaleDateString("it-IT")} /> : null}
        </section>

        <section className="mb-6 grid gap-6 sm:grid-cols-2">
          <Party label="Parte venditrice" p={seller} pct={deal.commission_seller_pct} amount={c.seller} />
          <Party label="Parte acquirente" p={buyer} pct={deal.commission_buyer_pct} amount={c.buyer} />
        </section>

        <section className="border-t border-rule pt-4 text-right">
          <p className="text-meta uppercase text-ink-mute print:text-black">Totale provvigioni</p>
          <p className="font-mono text-[26px] font-bold">{eur(c.total)}</p>
          <p className="text-meta text-ink-mute print:text-black">Importi al netto di IVA se non diversamente pattuito.</p>
        </section>

        <footer className="mt-8 border-t border-rule-soft pt-3 text-meta text-ink-mute print:text-black">
          {data.disclaimer}
        </footer>
      </div>
    </div>
  );
}

function Row({ k, v }: { k: string; v: string }): JSX.Element {
  return (
    <p className="text-body"><span className="inline-block w-[110px] text-ink-mute print:text-black">{k}</span> <b>{v}</b></p>
  );
}

function Party({ label, p, pct, amount }: { label: string; p: { name: string; email: string | null; phone: string | null } | null; pct: number | null; amount: number }): JSX.Element {
  return (
    <div className="rounded border border-rule-soft p-4 print:border-black/20">
      <p className="mb-1 text-meta uppercase text-ink-mute print:text-black">{label}</p>
      <p className="text-body-strong">{p?.name ?? "—"}</p>
      {p?.email ? <p className="text-meta text-ink-soft print:text-black">{p.email}</p> : null}
      {p?.phone ? <p className="text-meta text-ink-soft print:text-black">{p.phone}</p> : null}
      <p className="mt-2 font-mono text-body">
        {pct != null ? `${pct}% → ` : ""}<b>{eur(amount)}</b>
      </p>
    </div>
  );
}
